/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, { useEffect, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { bool, func, object } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import MarkdownText from './Partials/MarkdownText';
import InputField from './Partials/InputField';
import noop from '../../../../../helpers/noop';
import { setOfferInboxOffers } from '../../../../../../state/ducks/App/App-Actions';
import { getOfferInboxShowBonusOffer, getOfferInboxOfferPayloadById } from '../../../../../../state/ducks/App/App-Selectors';
import OfferButton from './Partials/OfferButton';
import BonusBanner from './Partials/BonusBanner';
import useOfferInboxImpression from './useOfferInboxImpression';
// eslint-disable-next-line import/no-cycle
import OfferGatewayDesign from './OfferGatewayDesign';

const useStyles = makeStyles((theme) => ({
    container: (styles) => ({
        border: '1px solid transparent',
        borderImage: 'repeating-linear-gradient(45deg, #D9D9D9 0 5px, transparent 5px 10px) 1',
        borderImageSlice: 1,
        borderRadius: '6px',
        padding: '18px',
        backgroundColor: styles?.backgroundColor,
    }),
    bonusContainer: (styles) => ({
        borderTop: '1px solid #d9d9d9',
        marginTop: '20px',
        paddingTop: '5px',
        backgroundColor: styles?.backgroundColor,
    }),
    text: {
        margin: '8px 0',
    },
    marginTop: {
        marginTop: '28px',
    },
    inlineBlock: {
        display: 'inline-block',
    },
}));

const WithImageDesign = ({
    offer, notFirst, trackClickThrough, isBonusOfferFound,
}) => {
    const isOfferGateway = offer?.type === 'offer-gateway';
    const offerContent = isOfferGateway ? offer?.content : offer;
    const structure = offerContent?.structure || [];
    const offerUID = offerContent?.uid;
    const offerData = useSelector((state) => getOfferInboxOfferPayloadById(state, offerUID));
    const shouldShowBonusOffer = useSelector(getOfferInboxShowBonusOffer) || offerContent?.show_bonus_offer;
    const [labelState, setLabelState] = useState('default');

    useOfferInboxImpression(offer);

    const classes = useStyles({
        backgroundColor: offerContent?.background_color?.color || '',
    });

    const dispatch = useDispatch();

    const inputFormId = offerContent?.input_form_id;

    useEffect(() => {
        const stateMap = {};
        if (!offerData?.buttonState) {
            structure?.forEach((item) => {
                if (item?.button) {
                    const { button_id, button_state } = item.button;
                    // Only add if button_id is not null/undefined and not already added
                    if (button_id !== null && !(button_id in stateMap)) {
                        stateMap[button_id] = button_state;
                    }
                }
            });

            const offerPayload = {
                offerId: offerUID,
                offer: {
                    buttonState: stateMap,
                },
            };

            dispatch(setOfferInboxOffers(offerPayload));
        }
    }, []);

    const renderStructureItem = (item, index) => {
        const keys = Object.keys(item);
        const data = item[keys[0]];

        switch (keys[0]) {
            case 'text':
                return <MarkdownText key={index} data={data} />;

            case 'image':
                return (
                    <img
                        key={index}
                        src={data?.file?.url}
                        alt={item.alt || 'Offer Image'}
                        style={{ maxWidth: '100%' }}
                    />
                );

            case 'input_field':
                return (
                    <InputField
                        key={index}
                        data={data}
                        className={classes.text}
                        offer={offer}
                        formId={inputFormId}
                    />
                );

            case 'button':
                return (
                    <OfferButton
                        key={index}
                        buttonData={data}
                        offer={offer}
                        className={`${data?.cta_button?.full_width ? '' : classes.inlineBlock}`}
                        trackClickThrough={trackClickThrough}
                    />
                );

            case 'bonus_banner':
                if (data?.label_state !== labelState || shouldShowBonusOffer) return null;
                return (
                    <BonusBanner key={index} data={data} setLabelState={setLabelState} />
                );

            default:
                return null;
        }
    };

    return (
        <>
            <div className={`${isBonusOfferFound ? classes.bonusContainer : classes.container} ${notFirst ? classes.marginTop : ''}`}>
                {
                    structure.map((item, index) => (renderStructureItem(item, index)))
                }
                {shouldShowBonusOffer && <OfferGatewayDesign offer={offerContent?.bonusOffer} isBonusOffer />}
            </div>
        </>
    );
};

WithImageDesign.propTypes = {
    offer: object,
    notFirst: bool,
    trackClickThrough: func,
    isBonusOfferFound: bool,
};

WithImageDesign.defaultProps = {
    offer: {},
    notFirst: false,
    trackClickThrough: noop,
    isBonusOfferFound: false,
};

export default WithImageDesign;
